import React, { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { ArrowLeft, Mail, ShieldCheck } from "lucide-react";
import accountApi from "@/lib/accountApiClient";
import { useLang } from "@/lib/i18n";
import useSEO from "@/lib/useSEO";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

export default function AccountForgotPassword() {
  const { lang, t } = useLang();
  const location = useLocation();
  const [email, setEmail] = useState(() => String(location.state?.email || ""));
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState("");

  useSEO({
    title: `${t("account.forgot.title")} — Mindovia`,
    description: t("account.forgot.description"),
    url: null,
    robots: "noindex, nofollow, noarchive",
  });

  const submit = async (event) => {
    event.preventDefault();
    setError("");
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) {
      setError(t("account.validation.email"));
      return;
    }
    setSubmitting(true);
    try {
      await accountApi.post("/account/auth/forgot-password", { email: email.trim(), lang });
      setSent(true);
    } catch (requestError) {
      setError(requestError?.response?.status === 429
        ? t("account.recovery.error.rate_limit")
        : t("account.error.network"));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-[70vh] px-4 py-12 sm:px-6 sm:py-16" data-wrap="responsive">
      <section className="mx-auto max-w-md rounded-3xl border border-border bg-card p-6 shadow-lg sm:p-8">
        <div className="grid h-11 w-11 place-items-center rounded-full bg-sage-100 text-teal-800"><Mail className="h-5 w-5" /></div>
        <h1 className="mt-5 font-display text-3xl tracking-tight">{t("account.forgot.title")}</h1>
        <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{t("account.forgot.description")}</p>

        {sent ? (
          <div className="mt-7" aria-live="polite" data-testid="forgot-password-success">
            <div className="rounded-2xl border border-sage-200 bg-sage-50 p-4 text-sm leading-relaxed text-teal-900">
              {t("account.forgot.sent")}
            </div>
            <p className="mt-4 flex items-start gap-2 text-xs leading-relaxed text-muted-foreground">
              <ShieldCheck className="mt-0.5 h-4 w-4 shrink-0" /> {t("account.forgot.privacy")}
            </p>
          </div>
        ) : (
          <form onSubmit={submit} className="mt-7 space-y-5" noValidate>
            <div className="space-y-2">
              <Label htmlFor="forgot-email">{t("account.email")}</Label>
              <Input id="forgot-email" type="email" autoComplete="email" inputMode="email" required value={email} onChange={(event) => setEmail(event.target.value)} className="h-12" />
            </div>
            {error && <div role="alert" className="rounded-xl border border-destructive/30 bg-destructive/5 p-3 text-sm text-destructive">{error}</div>}
            <Button type="submit" disabled={submitting} aria-busy={submitting} className="h-12 w-full rounded-full bg-teal-800 text-cream-50 hover:bg-teal-700">
              {submitting ? t("account.working") : t("account.forgot.submit")}
            </Button>
          </form>
        )}
        <Link to="/login" className="mt-7 inline-flex items-center gap-2 text-sm font-medium text-terracotta-700 hover:underline">
          <ArrowLeft className="h-4 w-4" /> {t("account.forgot.back")}
        </Link>
      </section>
    </div>
  );
}
